import React, { useState, useEffect, useContext } from 'react';

// Enhanced asset mapping from local workspace folder
import banner1 from '../assets/banner1.jpg';
import banner2 from '../assets/banner2.jpg';
import banner3 from '../assets/banner3.jpg';
import banner4 from '../assets/banner4.jpg';
import { calculateNewValue } from '@testing-library/user-event/dist/utils';
import { AppContext } from '../App';

const HomeBanner1 = () => {
  const images = [banner1, banner2, banner3, banner4];
  const [currentSlide, setCurrentSlide] = useState(0);
  const appContext = useContext(AppContext);

  // Micro-slider automation loop (3-second intervals)
  useEffect(() => {
    const slideInterval = setInterval(() => {
      setCurrentSlide((prevSlide) => (prevSlide + 1) % images.length);
    }, 3000);
    return () => clearInterval(slideInterval);
  }, [images.length]);

  return (
    <section style={{...styles.heroSection, backgroundImage: `url(${images[currentSlide]})`}}>
      {/* Premium Balanced Core Mask Overlays */}
      <div style={styles.colorOverlay}></div>
      <div style={styles.gradientOverlay}></div>

      {/* Bootstrap Native Fluid Container */}
      <div className="container" style={styles.containerFix}>
        {/* Bootstrap Responsive Row Layout */}
        <div className="row align-items-center gy-5">
          
          {/* Main Branding Block Content - Moves from 7-cols to full width on mobile/tablet */}
          <div className="col-12 col-lg-7 text-start">
            <div style={styles.heroContent}>
              
              <h1 style={styles.heroTitle} className="display-4 fw-medium text-white mb-4">
                Transforming <span style={styles.accentText}>Resources</span> <br />
                into Opportunities
              </h1>
              
              <p style={styles.heroDesc} className="lead mb-4">
                Tanzavibe Holdings Company Limited is established to create sustainable value through 
                agribusiness, water infrastructure, and strategic investments that drive inclusive 
                economic growth and community development across Tanzania.
              </p>
              
              <div style={styles.heroActions} className="d-flex flex-wrap gap-3 mb-5">
                <a onClick={() => appContext.navTo({ item: 'services' })} className="d-none d-md-inline-flex btn text-uppercase" style={styles.btnPrimary}>
                  Our Business Divisions
                </a>
                <a  className="btn text-uppercase" style={styles.btnOutline} onClick={() => appContext.navTo({ item: 'about_us' })}>
                  Learn More
                </a>
              </div>
              
              {/* Responsive Metric Badges */}
              <div style={styles.heroStats} className="row g-3 pt-4">
                <div className="col-6 col-sm-4">
                  <div style={styles.statValue}>Agribusiness</div>
                  <div style={styles.statLabel}>Value Addition & Processing</div>
                </div>
                <div className="col-6 col-sm-4">
                  <div style={styles.statValue}>Infrastructure</div>
                  <div style={styles.statLabel}>Professional Well Drilling</div>
                </div>
                <div className="col-12 col-sm-4 d-none d-sm-block">
                  <div style={styles.statValue}>Investments</div>
                  <div style={styles.statLabel}>Strategic Corporate Growth</div>
                </div>
              </div>
            </div>
          </div>

          {/* Upgraded Card Display Panel with Automated Slider Viewport */}
          <div className="col-12 col-lg-5">
            <div style={styles.corporateCard} className="w-100">

              {/* Minimal Slider Carousel Layer */}
              <div style={styles.sliderFrame} className="position-relative overflow-hidden w-100">
                <img 
                  src={images[currentSlide]} 
                  alt={`Tanzavibe Operational View ${currentSlide + 1}`} 
                  style={styles.sliderImg} 
                  className="w-100 h-100"
                />
                <div style={styles.sliderIndicators} className="position-absolute bottom-0 end-0 m-3 d-flex gap-1">
                  {images.map((_, idx) => (
                    <span 
                      key={idx} 
                      style={{
                        ...styles.indicatorDot, 
                        backgroundColor: idx === currentSlide ? '#ED8936' : 'rgba(255, 255, 255, 0.3)'
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Minimalist Corporate Meta Stack */}
              <div className="mb-3">
                <div style={styles.companyName} className="text-white fw-semibold">TANZAVIBE</div>
                <span style={styles.locationTag} className="fw-medium d-inline-block mt-1">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '6px', verticalAlign: '-1px' }}>
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                  </svg>
                  Arusha · Plot No. 1, Olkerien
                </span>
              </div>

              <div style={styles.minimalistMeta} className="pt-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span style={styles.metaLabel}>Holdings Focus</span>
                  <span style={styles.metaValueText}>Agribusiness & Infrastructure</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

const styles = {
  heroSection: {
    minHeight: 'calc(100vh - var(--topBarHeight))',
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    padding: '50px 0',
    backgroundColor: '#0A0F18',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    overflow: 'hidden',
    fontFamily: "'Libre Franklin', sans-serif",
    transition: 'background-image 1s ease-in-out',
  },
  colorOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(10, 15, 24, 0.85)',
    zIndex: 1,
  },
  gradientOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(180deg, rgba(10,15,24,0.92) 0%, rgba(10,15,24,0.65) 50%, rgba(10,15,24,0.95) 100%)',
    zIndex: 1,
  },
  containerFix: {
    position: 'relative',
    zIndex: 2,
  },
  heroContent: {
    width: '100%',
  },
  heroBadge: {
    gap: '10px',
    padding: '10px 18px',
    backgroundColor: '#111A2E',
    border: '1px solid #1E2D4A',
    borderRadius: '4px',
    fontSize: '11px',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: '#A0AEC0',
    marginBottom: '24px',
  },
  badgeDot: {
    width: '8px',
    height: '8px',
    backgroundColor: '#38A169',
    borderRadius: '50%',
  },
  heroTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    lineHeight: '1.25',
  },
  accentText: {
    color: '#ED8936',
  },
  heroDesc: {
    fontSize: '16.5px',
    color: '#CBD5E0',
    lineHeight: '1.75',
    maxWidth: '580px',
  },
  heroActions: {
    width: '100%',
  },
  btnPrimary: {
    padding: '12px 24px',
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.1em',
    backgroundColor: '#1A365D',
    color: '#FFF',
    border: '1px solid #2B6CB0',
    borderRadius: '4px',
  },
  btnOutline: {
    padding: '12px 24px',
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.1em',
    backgroundColor: 'transparent',
    color: '#ED8936',
    border: '1px solid #ED8936',
    borderRadius: '4px',
  },
  heroStats: {
    borderTop: '1px solid #1E2D4A',
  },
  statValue: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: '22px',
    color: '#ED8936',
    fontWeight: '600',
    marginBottom: '4px',
  },
  statLabel: {
    fontSize: '11.5px',
    color: '#718096',
    lineHeight: '1.4',
  },
  corporateCard: {
    backgroundColor: 'rgba(17, 26, 46, 0.88)',
    backdropFilter: 'blur(16px)',
    border: '1px solid rgba(30, 45, 74, 0.7)',
    padding: '24px',
    borderRadius: '8px',
    boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
  },
  cardLabel: {
    fontSize: '10.5px',
    textTransform: 'uppercase',
    letterSpacing: '0.12em',
    color: '#718096',
  },
  cardLive: {
    fontSize: '10.5px',
    color: '#38A169',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    fontWeight: '600',
  },
  livePulse: {
    width: '6px',
    height: '6px',
    backgroundColor: '#38A169',
    borderRadius: '50%',
  },
  sliderFrame: {
    height: '190px',
    borderRadius: '4px',
    backgroundColor: '#0A0F18',
    marginBottom: '20px',
  },
  sliderImg: {
    objectFit: 'cover',
    transition: 'opacity 0.6s ease-in-out',
  },
  indicatorDot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
  },
  companyName: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: '32px',
    lineHeight: '1',
  },
  locationTag: {
    color: '#ED8936',
    fontSize: '12px',
    letterSpacing: '0.04em',
  },
  minimalistMeta: {
    borderTop: '1px solid #1E2D4A',
  },
  metaLabel: {
    fontSize: '10.5px',
    color: '#4A5568',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  metaValueText: {
    fontSize: '12.5px',
    color: '#E2E8F0',
    fontWeight: '500',
  },
};

export default HomeBanner1;